import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["$indeterminate", "$disabled"],
  _excluded2 = ["$readOnly", "$indeterminate"],
  _excluded3 = ["data-vr-focus", "aria-label", "aria-labelledby", "autoFocus", "checked", "defaultChecked", "children", "color", "description", "disabled", "id", "indeterminate", "name", "onChange", "readOnly", "size", "tabIndex", "value", "alignment", "descriptionLayout", "inline", "innerPadding"];
import { forwardRef, useContext, useState } from 'react';
import styled from 'foundations-theming/utils';
import { FieldsetContext } from '../../context/FieldsetContext';
import { Checkmark } from './Checkmark';
import { getTabIndex } from '../../utils/TabIndex';
import { ToggleInputWrapper } from '../shared-components/ToggleInputWrapper';
import { uiFocus } from '../../utils/color';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import devLogger from 'react-utils/devLogger';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useIsDisplayOptimized } from '../../context';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const CheckboxWrapper = styled(ToggleInputWrapper)(({
  $cssStyles,
  $hasChildren,
  $inline,
  $innerPadding,
  $isDisplayOptimized,
  description,
  descriptionLayout,
  theme
}) => {
  return Object.assign({
    userSelect: 'none'
  }, description && descriptionLayout === 'vertical' && {
    flexDirection: 'column',
    flexWrap: 'wrap',
    marginTop: '6px'
  }, $hasChildren && {
    height: theme.space[500],
    width: theme.space[500]
  }, ($hasChildren && !$inline || $innerPadding === 'none') && {
    display: 'block',
    minHeight: '0'
  }, $isDisplayOptimized && {
    minHeight: '40px'
  }, $cssStyles);
});
const CheckboxIndicator = styled.span.withConfig({
  displayName: "Checkbox__CheckboxIndicator",
  componentId: "sc-1nupysh-0"
})(_ref => {
  let {
      $indeterminate,
      $disabled
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = rest.theme;
  const borderBase = `${theme.space[50]} solid`;
  let borderColor = '';
  if ($indeterminate && $disabled) {
    borderColor = theme.color['text-interactive-default-disabled'];
  } else if ($indeterminate) {
    borderColor = theme.color['fill-secondary-default'];
  } else {
    borderColor = theme.color['fill-tertiary-pressed'];
  }
  return {
    flexShrink: 0,
    backgroundColor: theme.color['fill-surface-default'],
    color: theme.color['fill-tertiary-pressed'],
    height: '1.25em',
    left: '0.0625em',
    position: 'relative',
    width: '1.25em',
    border: `${borderBase} ${borderColor}`,
    borderRadius: theme.space[100],
    transition: 'all 150ms ease-out',
    '&:hover': {
      backgroundColor: `${theme.color['fill-tertiary-default']} !important`
    }
  };
});
const CheckboxIcon = styled.span.withConfig({
  displayName: "Checkbox__CheckboxIcon",
  componentId: "sc-1nupysh-1"
})(["align-items:center;display:flex;justify-content:center;bottom:0;left:0;opacity:0;position:absolute;right:0;top:0;> svg{fill:currentColor;width:94%;}"]);
const CheckboxInput = styled.input.withConfig({
  displayName: "Checkbox__CheckboxInput",
  componentId: "sc-1nupysh-2"
})(_ref2 => {
  let {
      $readOnly,
      $indeterminate
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const theme = rest.theme;
  const $color = rest.$color;
  return Object.assign({
    // sr-only class styles
    position: 'absolute !important',
    top: '0 !important',
    width: `${theme.space[25]} !important`,
    height: `${theme.space[25]} !important`,
    margin: `-${theme.space[25]} !important`,
    padding: '0 !important',
    overflow: 'hidden !important',
    clip: 'rect(0, 0, 0, 0) !important',
    border: '0 !important',
    // hubspot-enable-focus-styles class styles
    '&:focus-visible': {
      [`& + ${CheckboxIndicator}`]: {
        boxShadow: 'rgba(0, 208, 228, 0.3) 0px 0px 4px 1px, rgb(0, 208, 228) 0px 0px 0px 1px !important'
      }
    },
    '&:checked': {
      [`& + ${CheckboxIndicator}`]: {
        borderColor: 'currentColor',
        color: $color ? theme.color[$color] : theme.color['fill-secondary-default']
      },
      [`+ ${CheckboxIndicator} ${CheckboxIcon}:first-child`]: {
        fill: theme.color['fill-secondary-default'],
        opacity: '1 !important'
      }
    }
  }, $indeterminate && {
    '&': {
      [`+ ${CheckboxIndicator}`]: {
        borderColor: 'currentColor',
        color: $color ? theme.color[$color] : theme.color['fill-secondary-default']
      }
    },
    '&:not(:checked)': {
      [`+ ${CheckboxIndicator} ${CheckboxIcon}:first-child`]: {
        opacity: 0,
        color: theme.color['fill-secondary-default']
      },
      [`+ ${CheckboxIndicator} ${CheckboxIcon}:last-child`]: {
        opacity: 1,
        color: theme.color['fill-secondary-default'],
        fill: theme.color['fill-secondary-default'],
        '> svg': {
          width: '100%'
        }
      }
    },
    '&:checked': {
      [`+ ${CheckboxIndicator} ${CheckboxIcon}:first-child`]: {
        opacity: 1,
        color: theme.color['fill-secondary-default']
      },
      [`+ ${CheckboxIndicator} ${CheckboxIcon}:last-child`]: {
        opacity: 0,
        color: theme.color['fill-secondary-default']
      }
    },
    '&:disabled': {
      [`+ ${CheckboxIndicator} ${CheckboxIcon}:last-child`]: {
        opacity: 1,
        color: theme.color['text-interactive-default-disabled']
      }
    }
  }, {
    '&:focus:not(:checked)': {
      [`+ ${CheckboxIndicator}`]: {
        borderColor: theme.color['fill-tertiary-pressed']
      }
    },
    '&:focus': {
      [`&:not([readonly]) + ${CheckboxIndicator}`]: Object.assign({
        borderColor: 'currentColor'
      }, !$readOnly && uiFocus),
      [`&[readonly] + ${CheckboxIndicator}`]: {
        outline: 'auto'
      }
    }
  });
});
const CheckboxText = styled.span.withConfig({
  displayName: "Checkbox__CheckboxText",
  componentId: "sc-1nupysh-3"
})(({
  $small,
  theme
}) => {
  return Object.assign({}, $small && {
    fontSize: theme.typography['body-100'].fontSize
  });
});
export const Checkbox = /*#__PURE__*/forwardRef((_ref3, ref) => {
  let {
      'data-vr-focus': dataVrFocus,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      autoFocus = false,
      checked: checkedProp,
      defaultChecked = false,
      children,
      color,
      description,
      disabled,
      id,
      indeterminate = false,
      name,
      onChange,
      readOnly = false,
      size,
      tabIndex,
      value,
      alignment = 'start',
      descriptionLayout = 'vertical',
      inline = false,
      innerPadding = 'default'
    } = _ref3,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  const {
    size: fieldSize,
    disabled: fieldDisabled
  } = useContext(FieldsetContext);
  const isDisplayOptimized = useIsDisplayOptimized();
  const descriptionId = useUniqueId('checkbox-');
  const childWrapperId = useUniqueId('checkbox-content-');
  const computedDisabled = disabled || fieldDisabled;
  const computedSize = size || fieldSize;
  const isControlled = checkedProp !== undefined;
  const [internalChecked, setInternalChecked] = useState(defaultChecked);
  const checkedValue = isControlled ? checkedProp : internalChecked;
  const handleChange = event => {
    if (!isControlled) {
      setInternalChecked(event.target.checked);
    }
    if (onChange) onChange(event);
  };
  const ariaLabelledByIds = `${ariaLabelledBy !== null && ariaLabelledBy !== void 0 ? ariaLabelledBy : ''} ${children ? childWrapperId : ''}`.trim();
  if (process.env.NODE_ENV !== 'production') {
    if (children == null && !ariaLabel && !ariaLabelledBy) {
      devLogger.warn({
        message: "Checkbox: `aria-label` or `aria-labelledby` is required if you don't define `children`.",
        key: 'Checkbox: no label'
      });
    }
    if (ariaLabel && (children || ariaLabelledBy)) {
      devLogger.warn({
        message: 'Checkbox: `aria-label` should not be used with `children` or `aria-labelledby`.',
        key: 'Checkbox: aria-label hides children',
        url: 'https://git.hubteam.com/HubSpot/Components/pull/8473/files#r1011220'
      });
    }
  }
  const indeterminateAttributes = indeterminate && !checkedValue ? {
    'aria-checked': 'mixed'
  } : {};
  const {
    domProps,
    tokenProps,
    callbackProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(CheckboxWrapper, Object.assign({
    checked: checkedValue,
    childWrapperId: childWrapperId,
    descriptionId: descriptionId,
    description: description,
    descriptionLayout: descriptionLayout,
    $description: description,
    $descriptionLayout: descriptionLayout,
    disabled: computedDisabled,
    $hasChildren: children == null,
    inline: inline,
    $inline: inline,
    $innerPadding: innerPadding,
    $isDisplayOptimized: isDisplayOptimized,
    $cssStyles: cssStyles,
    alignment: alignment,
    input: /*#__PURE__*/_jsx(CheckboxInput, Object.assign({
      "data-vr-focus": dataVrFocus,
      "aria-describedby": description ? descriptionId : undefined,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledByIds || undefined,
      autoFocus: autoFocus,
      checked: checkedValue,
      disabled: computedDisabled,
      id: id,
      name: name,
      onChange: evt => {
        if (disabled || readOnly) {
          evt.preventDefault();
          return;
        }
        handleChange(evt);
      },
      $color: color,
      $readOnly: readOnly,
      readOnly: readOnly // to pass through to DOM
      ,
      tabIndex: getTabIndex(readOnly || disabled !== undefined, tabIndex),
      type: "checkbox",
      value: value,
      $indeterminate: indeterminate
    }, indeterminateAttributes)),
    indicator: /*#__PURE__*/_jsxs(CheckboxIndicator, {
      $indeterminate: indeterminate,
      $disabled: disabled,
      "data-indicator": true,
      children: [/*#__PURE__*/_jsx(CheckboxIcon, {
        children: /*#__PURE__*/_jsx(Checkmark, {})
      }), /*#__PURE__*/_jsx(CheckboxIcon, {
        children: /*#__PURE__*/_jsx("svg", {
          role: "presentation",
          viewBox: "0 0 14 14",
          xmlns: "http://www.w3.org/2000/svg",
          children: /*#__PURE__*/_jsx("path", {
            d: "M 2 6 L12 6 L12 9 L2 9 L2 6 z"
          })
        })
      })]
    }),
    onChange: handleChange,
    readOnly: readOnly,
    ref: ref,
    size: computedSize,
    "data-checkbox": true
  }, domProps, callbackProps, {
    children: children && /*#__PURE__*/_jsx(CheckboxText, {
      $small: computedSize === 'sm' || isDisplayOptimized,
      children: children
    })
  }));
});